/**
 * As chamadas da tela de revisão (`TP-050`, T9/T10/T11).
 *
 * Tudo o que a tela sabe sobre `/revisao`, `/regras-contratuais`,
 * `/itens-contratuais` e as rotas de sessão de `/fontes/{id}` passa por
 * aqui — mesma disciplina de `features/contratos/api/contratos.ts`.
 */

import { queryOptions } from '@tanstack/react-query';

import { requisitar } from '../../../../shared/api/clienteHttp';
import { mapearEstadoDaFonte } from '../../api/mapeadores';
import type { EstadoDaFonte } from '../../api/tipos';
import {
  mapearDiffDaFonte,
  mapearPaginaDaFonte,
  mapearRegraDaFila,
  mapearRegraRevisada,
} from './mapeadores';
import type {
  AprovacaoEmLoteApi,
  DiffDaFonte,
  EstadoDaFonteAtivadaApi,
  PaginaDaFonte,
  PaginaDaFonteApi,
  RegraDaFila,
  RegraDaFilaApi,
  RegraRevisada,
  RegraRevisadaApi,
  SourceDiffApi,
  TipoDeRegra,
  EtapaDeReprocesso,
} from './tipos';

export const chavesDeRevisao = {
  raiz: ['revisao'] as const,
  fila: (fonteId: string) => [...chavesDeRevisao.raiz, 'fila', fonteId] as const,
  diff: (fonteId: string) => [...chavesDeRevisao.raiz, 'diff', fonteId] as const,
  paginas: (fonteId: string) => [...chavesDeRevisao.raiz, 'paginas', fonteId] as const,
};

export function opcoesDaFila(fonteId: string) {
  return queryOptions({
    queryKey: chavesDeRevisao.fila(fonteId),
    queryFn: ({ signal }): Promise<RegraDaFila[]> => {
      const parametros = new URLSearchParams({ fonteId });
      return requisitar<RegraDaFilaApi[]>(`/revisao/regras?${parametros.toString()}`, { sinal: signal }).then(
        (itens) => itens.map(mapearRegraDaFila),
      );
    },
  });
}

export function opcoesDoDiff(fonteId: string) {
  return queryOptions({
    queryKey: chavesDeRevisao.diff(fonteId),
    queryFn: ({ signal }): Promise<DiffDaFonte> =>
      requisitar<SourceDiffApi>(`/fontes/${encodeURIComponent(fonteId)}/diff`, { sinal: signal }).then(mapearDiffDaFonte),
  });
}

/**
 * `GET /fontes/{id}/paginas-revisao` — ver a nota de `api/tipos.ts`
 * (`DI34`, `TP-050`): a rota de sessão que fecha o gap de `DI28` (a de
 * máquina, `/fontes/{id}/paginas`, devolvia `401` para a tela). Mesmo
 * formato de resposta, mais `observacao`.
 */
export function opcoesDasPaginas(fonteId: string) {
  return queryOptions({
    queryKey: chavesDeRevisao.paginas(fonteId),
    queryFn: ({ signal }): Promise<PaginaDaFonte[]> =>
      requisitar<PaginaDaFonteApi[]>(`/fontes/${encodeURIComponent(fonteId)}/paginas-revisao`, { sinal: signal }).then(
        (paginas) => paginas.map(mapearPaginaDaFonte),
      ),
  });
}

export async function aprovarRegra(regraId: string): Promise<RegraRevisada> {
  return requisitar<RegraRevisadaApi>(`/regras-contratuais/${encodeURIComponent(regraId)}/aprovar`, {
    method: 'POST',
  }).then(mapearRegraRevisada);
}

export async function rejeitarRegra(regraId: string, motivo: string): Promise<RegraRevisada> {
  return requisitar<RegraRevisadaApi>(`/regras-contratuais/${encodeURIComponent(regraId)}/rejeitar`, {
    method: 'POST',
    corpo: { motivo },
  }).then(mapearRegraRevisada);
}

export async function ajustarRegra(regraId: string, escopo: unknown, efeito: unknown): Promise<RegraRevisada> {
  return requisitar<RegraRevisadaApi>(`/regras-contratuais/${encodeURIComponent(regraId)}/ajustar`, {
    method: 'POST',
    corpo: { escopo, efeito },
  }).then(mapearRegraRevisada);
}

export async function aprovarLote(fonteId: string, tipo: TipoDeRegra): Promise<number> {
  return requisitar<AprovacaoEmLoteApi>('/revisao/regras/aprovar-lote', {
    method: 'POST',
    // A API lê o tipo pelo token do banco (`DbTokens.ContractRuleType.TryFromToken`), não pelo
    // nome em inglês que ela devolve nas leituras; o token é o próprio `TipoDeRegra`.
    corpo: { fonteId, tipo },
  }).then((resposta) => resposta.aprovadas);
}

export async function aprovarItem(itemId: string): Promise<void> {
  await requisitar(`/itens-contratuais/${encodeURIComponent(itemId)}/aprovar`, { method: 'POST' });
}

/** Aprova de uma vez os itens da fonte que o ativar cobra (unidade de cobrança com confiança média ou baixa). */
export async function aprovarItensEmLote(fonteId: string): Promise<number> {
  return requisitar<AprovacaoEmLoteApi>(`/fontes/${encodeURIComponent(fonteId)}/itens/aprovar-lote`, {
    method: 'POST',
  }).then((resposta) => resposta.aprovadas);
}

export async function ativarFonte(fonteId: string): Promise<EstadoDaFonte> {
  return requisitar<EstadoDaFonteAtivadaApi>(`/fontes/${encodeURIComponent(fonteId)}/ativar`, {
    method: 'POST',
  }).then((resposta) => mapearEstadoDaFonte(resposta.status));
}

export async function reprocessarFonte(fonteId: string, etapa: EtapaDeReprocesso): Promise<EstadoDaFonte> {
  const parametros = new URLSearchParams({ etapa });
  return requisitar<EstadoDaFonteAtivadaApi>(`/fontes/${encodeURIComponent(fonteId)}/reprocessar?${parametros.toString()}`, {
    method: 'POST',
  }).then((resposta) => mapearEstadoDaFonte(resposta.status));
}
